import React, { useState, useMemo, useCallback } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { formatNameForDisplay } from '../../utils/nameUtils';
import AnimatedNumber from '../../utils/AnimatedNumber';
import { detectPairType } from '../../utils/pairTypeDetector';
import { getPlayerNameColor, formatPlayerName, formatEloForUi, sortTeamMemberIdsForDisplay } from '../../utils/playerUtils';
import PlayerSelectionModal from '../players/PlayerSelectionModal';
import EloChange from '../common/EloChange';
import PlayerActionDropdown from './PlayerActionDropdown';
import PlayerSelectionDropdown from './PlayerSelectionDropdown';
import { Typography } from '../common/Typography';
import StatusBadge from '../common/StatusBadge';

function CourtEmptySlotDroppable({ droppableId, courtPlayerDragEnabled, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: droppableId, disabled: !courtPlayerDragEnabled });
  return (
    <div
      ref={setNodeRef}
      className={`w-full rounded ${courtPlayerDragEnabled && isOver ? 'ring-2 ring-primary/50 ring-inset' : ''}`}
    >
      {children}
    </div>
  );
}

function CourtOccupiedSlotDnD({
  droppableId,
  playerId,
  courtPlayerDragEnabled,
  children,
}) {
  const draggableId = `drag-${droppableId}`;
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: droppableId,
    disabled: !courtPlayerDragEnabled,
  });
  const { setNodeRef: setDragRef, attributes, listeners, transform, isDragging } = useDraggable({
    id: draggableId,
    disabled: !courtPlayerDragEnabled,
    data: { playerId, from: droppableId },
  });
  const setNodeRef = useCallback(
    (node) => {
      setDropRef(node);
      setDragRef(node);
    },
    [setDropRef, setDragRef]
  );
  const style = transform
    ? { transform: `translate3d(${transform.x}px,${transform.y}px,0)` }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`w-full rounded ${courtPlayerDragEnabled && isOver ? 'ring-2 ring-primary/40 ring-inset' : ''} ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      {typeof children === 'function'
        ? children({
            dragActivatorProps: courtPlayerDragEnabled ? { ...listeners, ...attributes } : null,
          })
        : children}
    </div>
  );
}

export default function TeamPanel({
  team = [],
  teamNumber,
  playersById = {},
  avgElo,
  isWinner = false,
  canRemove = false,
  onRemovePlayer,
  onAddPlayer,
  courtNumber,
  showEloChange = false,
  eloDeltas = {},
  isSocialSession = false,
  availablePlayers = [],
  waitingManagementPlayers = [],
  sessionPlayerStatus = {},
  score = null,
  nextAssignments = [],
  onRemoveFromUpcoming,
  playerStreaks = {},
  emptySlotsReadOnly = false,
  maxPlayersPerTeam = 2,
  density = 'comfortable',
  courtPlayerDragEnabled = false,
  /** When set (e.g. `upcoming-0`), slot ids avoid colliding with physical court numbers */
  slotDndPrefix = null,
  playerWaitMetrics = {},
  showEloTooltip = true,
  pegBoardQueue = null,
}) {
  const useSinglesElo = maxPlayersPerTeam === 1;
  const compact = density === 'compact';
  const slotId = (teamNum, idx) =>
    slotDndPrefix
      ? `${slotDndPrefix}-team${teamNum}-slot-${idx}`
      : `court-${courtNumber}-team${teamNum}-slot-${idx}`;
  const orderedTeam = useMemo(
    () => sortTeamMemberIdsForDisplay(team, playersById),
    [team, playersById]
  );
  const [showPlayerSelection, setShowPlayerSelection] = useState(false);

  const isTeam1 = teamNumber === 1;
  
  // Accent border is the ONLY team differentiator — no background fill.
  // This keeps the card surface clean and avoids competing colour regions.
  const accentClass = isWinner
    ? 'border-l-[3px] border-success'
    : isTeam1
      ? 'border-l-[3px] border-team1'
      : 'border-l-[3px] border-team2';

  const textColor = 'text-text-primary';

  const teamLabelColor = isWinner
    ? 'text-success-dark'
    : isTeam1
      ? 'text-team1'
      : 'text-team2';

  // Detect pair type for this team (only for doubles)
  const teamPlayers = orderedTeam.map(id => playersById[id]).filter(Boolean);
  const pairType =
    maxPlayersPerTeam === 2 && teamPlayers.length === 2 ? detectPairType(teamPlayers) : null;

  // Debug logging removed for cleaner console

  return (
    <div
      className={`flex w-full flex-col ${compact ? 'pl-1.5 pr-2' : 'pl-3 pr-3 sm:pr-3.5'} ${accentClass}`}
    >
      <div className={`flex items-center justify-between gap-2 ${compact ? 'mb-0.5' : 'mb-1.5'}`}>
        <span
          className={`flex items-center gap-0.5 font-semibold ${compact ? 'text-xs' : 'text-sm lg:text-base lg:gap-1.5'} ${teamLabelColor}`}
        >
          Team {teamNumber}
        </span>
        {showEloTooltip ? (
          <span
            className={`flex shrink-0 items-baseline gap-0.5 whitespace-nowrap text-right font-bold ${compact ? 'text-sm' : 'text-base lg:text-lg'} ${textColor}`}
          >
            {isSocialSession ? (
              <span className="text-text-muted font-semibold">elo:</span>
            ) : null}
            <span className="tabular-nums">{formatEloForUi(avgElo)}</span>
          </span>
        ) : null}
      </div>
      <ul className="flex flex-col w-full">
        {/* Render existing players with dropdown actions */}
        {orderedTeam.map((playerId) => {
          const player = playersById[playerId];
          if (!player) return null;

          const storageIndex = team.indexOf(playerId);
          if (storageIndex === -1) return null;

          // Get ELO change for this player
          const displayEloChange = eloDeltas && eloDeltas[playerId];

          // Find teammate for ELO calculation
          const teammate =
            maxPlayersPerTeam === 2 && orderedTeam.length === 2
              ? playersById[orderedTeam.find((id) => id !== playerId)]
              : null;

          return (
            <li key={playerId} className="w-full">
              <CourtOccupiedSlotDnD
                droppableId={slotId(teamNumber, storageIndex)}
                playerId={playerId}
                courtPlayerDragEnabled={courtPlayerDragEnabled}
              >
                {({ dragActivatorProps }) => (
                  <PlayerActionDropdown
                    player={player}
                    teamNumber={teamNumber}
                    courtNumber={courtNumber}
                    onRemove={canRemove ? onRemovePlayer : null}
                    availablePlayers={availablePlayers}
                    showEloChange={showEloChange}
                    eloDelta={displayEloChange}
                    teammate={teammate}
                    isSocialSession={isSocialSession}
                    playerStreaks={playerStreaks}
                    useSinglesElo={useSinglesElo}
                    dragActivatorProps={dragActivatorProps}
                    compact={compact}
                    waitMetricSlice={playerWaitMetrics[playerId] || null}
                    showEloTooltip={showEloTooltip}
                  />
                )}
              </CourtOccupiedSlotDnD>
            </li>
          );
        })}
        
        {orderedTeam.length < maxPlayersPerTeam && (
          Array.from({ length: maxPlayersPerTeam - orderedTeam.length }, (_, idx) => {
            const slotIndex = team.length + idx;
            return (
              <li key={`empty-slot-${idx}`} className="w-full">
                <CourtEmptySlotDroppable
                  droppableId={slotId(teamNumber, slotIndex)}
                  courtPlayerDragEnabled={courtPlayerDragEnabled}
                >
                  <PlayerSelectionDropdown
                    availablePlayers={availablePlayers}
                    waitingManagementPlayers={waitingManagementPlayers}
                    sessionPlayerStatus={sessionPlayerStatus}
                    onSelectPlayer={onAddPlayer}
                    courtNumber={courtNumber}
                    teamNumber={teamNumber}
                    isSocialSession={isSocialSession}
                    nextAssignments={nextAssignments}
                    onRemoveFromUpcoming={onRemoveFromUpcoming}
                    readOnly={emptySlotsReadOnly}
                    useSinglesElo={useSinglesElo}
                    compact={compact}
                    showEloTooltip={showEloTooltip}
                    pegBoardQueue={pegBoardQueue}
                  />
                </CourtEmptySlotDroppable>
              </li>
            );
          })
        )}
      </ul>
      
      {/* Player Selection Modal (fallback for non-social sessions) */}
      <PlayerSelectionModal
        isOpen={showPlayerSelection}
        onClose={() => setShowPlayerSelection(false)}
        availablePlayers={availablePlayers}
        onSelectPlayer={onAddPlayer}
        courtNumber={courtNumber}
        teamNumber={teamNumber}
        playersById={playersById}
        useSinglesElo={useSinglesElo}
        showEloTooltip={showEloTooltip}
      />
      
      {/* Winner text - appears under the winning team */}
      {isWinner && (
        <div className={`text-center ${compact ? 'mt-1' : 'mt-2'}`}>
          <StatusBadge variant="success" className="text-sm">
            🏆 Winner{score && ` (${score.team1Score}-${score.team2Score})`}
          </StatusBadge>
        </div>
      )}
    </div>
  );
}